import React from "react";
import { useMemo } from "react";
// @mui
import { Grid, Typography, Chip, Box, Tab } from "@mui/material";
// components
import GeoGraph from "./Map";
import ContentBox from "~src/components/ContentBox";
// type
import { CountryTrafficGrowth } from "~src/types/brand";
import CountryDetails from "./CountryDetails";
import {colors} from "~src/data/brandTabColors.ts"
import { BrandResult } from "../../SearchBar";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import CountryAdsDetails from "./CountryAdsDetails";
import usePermissions from "~src/hooks/usePermissions";
import Iconify from "~src/components/Iconify";
import { CompetitorAds } from "../../CompetitorAnalytics";

// ------------------------------------------------------------------------------------------------

export default function GeoMap({
  countryTrafficData,
  countryAdsData,
  loading,
  selectedBrands,
}: {
  countryTrafficData: CountryTrafficGrowth[][];
  countryAdsData: CompetitorAds | undefined;
  loading: boolean;
  selectedBrands: BrandResult[] | undefined;
}) {
  const { premiumCommunity } = usePermissions();
  const processCountryTrafficData = (data: CountryTrafficGrowth[][]) => {
    return data.map((subArray) => {
      return subArray?.map((each) => ({
        id: each.code,
        value: parseFloat(each.traffic),
      }));
    });
  };

  const processAdsData = (data: CompetitorAds) => {
    const result = [];

    for (const brandId in data.map_data) {
      if (Object.prototype.hasOwnProperty.call(data.map_data, brandId)) {
        const adsByCountry = data.map_data[brandId].ads_by_country;
        const processedAdsByCountry = adsByCountry.map((each) => ({
          id: each.ad_country || "Unknown",
          value: each.ad_count,
        }));
        result.push(processedAdsByCountry);
      }
    }

    return result;
  };

  const trafficGraphData = useMemo(
    () => processCountryTrafficData(countryTrafficData),
    [countryTrafficData],
  );

  const adsGraphData = useMemo(
    () => countryAdsData && processAdsData(countryAdsData),
    [countryAdsData],
  );

  const [value, setValue] = React.useState("1");

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  return (
    <ContentBox mb={3} mt={3}>
      <Typography variant="h5" sx={{
        mb: 1
      }}>
        Country Targeting
      </Typography>
      <TabContext value={value}>
        <Box>
          <TabList onChange={handleChange} aria-label="lab API tabs example">
            <Tab label="Traffic" value="1" />
            <Tab label="Ads Tracking" value="2" />
          </TabList>
        </Box>
        <TabPanel value="1">
          <Box>
            {!loading && (
              <Grid container sx={{ height: "100%" }} spacing={3}>
                {trafficGraphData && (
                  <Grid  sx={{ height: 424 }} size={{ xs: 12, md: 8 }} >
                    <GeoGraph
                      graphData={
                        !premiumCommunity && trafficGraphData.length > 3
                          ? trafficGraphData.slice(0, 3)
                          : trafficGraphData
                      }
                    />
                  </Grid>
                )}

                <Grid  size={{ xs: 12, md: 4 }} >
                  <CountryDetails
                    data={
                      !premiumCommunity && countryTrafficData.length > 3
                        ? countryTrafficData.slice(0, 3)
                        : countryTrafficData
                    }
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </TabPanel>
        <TabPanel value="2">
          <Box>
            {!loading && (
              <Grid container sx={{ height: "100%" }} spacing={3}>
                {adsGraphData && (
                  <Grid  sx={{ height: 424 }} size={{ xs: 12, md: 8 }} >
                    <GeoGraph
                      graphData={
                        !premiumCommunity && adsGraphData.length > 3
                          ? adsGraphData.slice(0, 3)
                          : adsGraphData
                      }
                    />
                  </Grid>
                )}

                <Grid  size={{ xs: 12, md: 4 }} >
                  <CountryAdsDetails data={countryAdsData} />
                </Grid>
              </Grid>
            )}
          </Box>
        </TabPanel>
      </TabContext>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mt: 2,
        }}
      >
        {selectedBrands &&
          selectedBrands.map((brand, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", mr: 2 }}
            >
              <Chip
                sx={{
                  height: "14px",
                  width: "14px",
                  borderRadius: "3px",
                  mr: 1,
                  backgroundColor: colors[index],
                }}
              />
              {!premiumCommunity && (index === 3 || index === 4) ? (
                <Iconify
                  sx={{ position: "relative" }}
                  icon={"ic:outline-lock"}
                />
              ) : (
                <Typography>{brand.brand_name}</Typography>
              )}
            </Box>
          ))}
      </Box>
    </ContentBox>
  );
}
